import { OpenAI } from 'openai';
import { OPENAI_API_KEY } from '../utils/env';

// Initialize OpenAI client with better error handling
console.log('API Key available:', !!OPENAI_API_KEY, 'Length:', OPENAI_API_KEY?.length);
if (!OPENAI_API_KEY) {
  console.error('OpenAI API key is missing. Please check your .env file and ensure REACT_APP_OPENAI_API_KEY is set.');
}

// Create OpenAI client with explicit configuration
const openai = new OpenAI({
  apiKey: OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
  baseURL: 'https://api.openai.com/v1'  // Explicitly set the base URL
});

export const openaiService = {
  chat: async (messages, tools = [], userLocation = null) => {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured. Please add REACT_APP_OPENAI_API_KEY to your .env file.');
    }

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: messages.map(msg => ({
          role: msg.role === 'assistant' ? 'assistant' : 'user',
          content: msg.content
        })),
        temperature: 0.7,
        max_tokens: 1000,
        tools: tools.map(tool => ({
          type: "function",
          function: {
            name: tool.name,
            description: tool.description,
            parameters: {
              type: "object",
              properties: {
                query: { type: "string" },
                location: tool.type === 'web_search_preview' ? {
                  type: "object",
                  properties: {
                    city: { type: "string" },
                    region: { type: "string" },
                    country: { type: "string" }
                  }
                } : undefined
              }
            }
          }
        }))
      });

      return {
        text: completion.choices[0]?.message?.content || '',
        raw: completion
      };
    } catch (error) {
      console.error('OpenAI chat error:', error);
      throw error;
    }
  },

  generateText: async (prompt) => {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured. Please add REACT_APP_OPENAI_API_KEY to your .env file.');
    }

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.7,
        max_tokens: 1000
      });

      return completion.choices[0]?.message?.content || '';
    } catch (error) {
      console.error('OpenAI text generation error:', error);
      throw error;
    }
  },

  generateImage: async (prompt) => {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured. Please add REACT_APP_OPENAI_API_KEY to your .env file.');
    }

    try {
      const response = await openai.images.generate({
        model: "dall-e-3",
        prompt: prompt,
        n: 1,
        size: "1024x1024"
      });

      return response.data[0].url;
    } catch (error) {
      console.error('OpenAI image generation error:', error);
      throw error;
    }
  },

  analyzeDocument: async (text) => {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured. Please add REACT_APP_OPENAI_API_KEY to your .env file.');
    }

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [
          {
            role: "user",
            content: `Please analyze the following document and provide a detailed summary:\n\n${text}`
          }
        ],
        temperature: 0.7,
        max_tokens: 1000
      });

      return completion.choices[0]?.message?.content || '';
    } catch (error) {
      console.error('OpenAI document analysis error:', error);
      throw error;
    }
  },

  generateSocialPost: async (prompt, platform) => {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured. Please add REACT_APP_OPENAI_API_KEY to your .env file.');
    }

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [
          {
            role: "user",
            content: `Generate a ${platform} post about: ${prompt}\nMake it engaging and platform-appropriate.`
          }
        ],
        temperature: 0.7,
        max_tokens: 1000
      });

      return completion.choices[0]?.message?.content || '';
    } catch (error) {
      console.error('OpenAI social post generation error:', error);
      throw error;
    }
  }
}; 